// Vmc.js - Ventilação mecânica controlada, via Tuya
//
// É a vista com mais leituras: temperatura e humidade dentro e fora, CO2 e
// vida do filtro. O aviso do topo é o que interessa quando algo corre mal,
// por isso aparece antes de tudo o resto.
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Fan, Thermometer, Droplets, Wind, Activity, Power,
  AlertCircle, AlertTriangle, Wrench,
} from 'lucide-react';
import { useTuya } from '../hooks/useTuya';
import ViewHeader from './ViewHeader';

// Limiares do eCO2 em ppm. Abaixo de 800 é ar de rua; acima de 1200 já se
// nota na concentração de quem está na sala.
const CO2_BOM = 800;
const CO2_MEDIO = 1200;

// A vida do filtro vem em dias. Abaixo de 15 avisa-se, a zero é urgente.
const FILTRO_AVISO = 15;
const FILTRO_BARRA_MAX = 200;

function Vmc({ onBack }) {
  const { t } = useTranslation();
  const { devices, loading, error, fetchDevices, togglePower } = useTuya();

  const vmc = devices.find((d) => d.device_type === 'vmc');

  const temAvaria = !!(vmc && vmc.fault_codes && vmc.fault_codes.length > 0);
  const filtroEsgotado = !!(vmc && vmc.filter_life <= 0);
  const filtroQuaseNoFim = !!(vmc && !filtroEsgotado && vmc.filter_life <= FILTRO_AVISO);
  const mostrarAviso = vmc && (temAvaria || filtroEsgotado || filtroQuaseNoFim);

  // O filtro tem precedência sobre a avaria: é o problema que o utilizador
  // consegue resolver sozinho.
  let avisoTitulo = '';
  let avisoMensagem = '';
  if (filtroEsgotado) {
    avisoTitulo = t('vmc.filtroEsgotado');
    avisoMensagem = t('vmc.filtroEsgotadoTexto');
  } else if (filtroQuaseNoFim) {
    avisoTitulo = t('vmc.filtroQuaseNoFim');
    avisoMensagem = t('vmc.filtroQuaseNoFimTexto', { dias: vmc?.filter_life });
  } else if (temAvaria) {
    avisoTitulo = t('vmc.avariaDetetada');
    avisoMensagem = t('vmc.avariaDetetadaTexto');
  }

  const classeAviso = filtroEsgotado
    ? 'severity-critical'
    : filtroQuaseNoFim
      ? 'severity-warning'
      : 'severity-fault';

  const qualidadeCo2 = (ppm) => {
    if (ppm < CO2_BOM) return { classe: 'good', texto: t('vmc.arExcelente') };
    if (ppm < CO2_MEDIO) return { classe: 'medium', texto: t('vmc.arModerado') };
    return { classe: 'bad', texto: t('vmc.arElevado') };
  };

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={Fan}
          iconClass="icon-vmc"
          titulo={t('vmc.titulo')}
          subtitulo={t('vmc.subtitulo')}
          onBack={onBack}
          onRefresh={fetchDevices}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!vmc ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('vmc.aCarregar')}</p>
          </div>
        ) : (
          <div className="vmc-container">
            {mostrarAviso && (
              <div className={`alarm-banner ${classeAviso}`}>
                <div className="alarm-banner-icon">
                  {filtroEsgotado || filtroQuaseNoFim ? <Wrench size={20} /> : <AlertTriangle size={20} />}
                </div>
                <div className="alarm-banner-content">
                  <div className="alarm-banner-title-row">
                    <span className="alarm-pulse-dot" />
                    <span className="alarm-banner-title">{avisoTitulo}</span>
                    {temAvaria && (
                      <span className="alarm-code-chip">{vmc.fault_codes.join(' · ')}</span>
                    )}
                  </div>
                  <span className="alarm-banner-message">{avisoMensagem}</span>
                </div>
              </div>
            )}

            <div className={`vmc-status-card ${vmc.online ? 'online' : 'offline'}`}>
              <div className="vmc-status-header">
                <div className="status-indicator">
                  <div className={`status-dot ${vmc.online ? 'online' : 'offline'}`} />
                  <span>{vmc.online ? t('comum.online') : t('comum.offline')}</span>
                </div>
                <div className={`power-badge ${vmc.power ? 'on' : 'off'}`}>
                  {vmc.power ? t('comum.ligadoMaiusc') : t('comum.desligadoMaiusc')}
                </div>
              </div>

              <div className="vmc-power-section">
                <button
                  className={`btn-power-vmc ${vmc.power ? 'on' : 'off'}`}
                  onClick={() => togglePower(vmc.device_id, vmc.power)}
                  disabled={loading || !vmc.online}
                >
                  <Power size={32} />
                  <span>{vmc.power ? t('vmc.desligarVmc') : t('vmc.ligarVmc')}</span>
                </button>
              </div>
            </div>

            <div className="vmc-metrics-grid">
              <div className="metric-card temp-indoor">
                <div className="metric-icon"><Thermometer size={32} /></div>
                <div className="metric-content">
                  <span className="metric-label">{t('vmc.temperaturaInterior')}</span>
                  <span className="metric-value">{vmc.temp_indoor}°C</span>
                </div>
              </div>

              <div className="metric-card temp-outdoor">
                <div className="metric-icon"><Thermometer size={32} /></div>
                <div className="metric-content">
                  <span className="metric-label">{t('vmc.temperaturaExterior')}</span>
                  <span className="metric-value">{vmc.temp_outdoor}°C</span>
                </div>
              </div>

              <div className="metric-card humidity-indoor">
                <div className="metric-icon"><Droplets size={32} /></div>
                <div className="metric-content">
                  <span className="metric-label">{t('vmc.humidadeInterior')}</span>
                  <span className="metric-value">{vmc.humidity_indoor}%</span>
                  <div className="humidity-bar">
                    <div className="humidity-fill" style={{ width: `${vmc.humidity_indoor}%` }} />
                  </div>
                </div>
              </div>

              <div className="metric-card humidity-outdoor">
                <div className="metric-icon"><Droplets size={32} /></div>
                <div className="metric-content">
                  <span className="metric-label">{t('vmc.humidadeExterior')}</span>
                  <span className="metric-value">{vmc.humidity_outdoor}%</span>
                  <div className="humidity-bar">
                    <div className="humidity-fill" style={{ width: `${vmc.humidity_outdoor}%` }} />
                  </div>
                </div>
              </div>

              <div className="metric-card co2">
                <div className="metric-icon"><Wind size={32} /></div>
                <div className="metric-content">
                  <span className="metric-label">eCO2</span>
                  <span className="metric-value">{vmc.eco2} ppm</span>
                  <span className={`co2-status ${qualidadeCo2(vmc.eco2).classe}`}>
                    {qualidadeCo2(vmc.eco2).texto}
                  </span>
                </div>
              </div>

              <div className={`metric-card filter ${vmc.filter_life < 20 ? 'warning' : ''}`}>
                <div className="metric-icon"><Activity size={32} /></div>
                <div className="metric-content">
                  <span className="metric-label">{t('vmc.vidaFiltro')}</span>
                  <span className="metric-value">{t('vmc.dias', { n: vmc.filter_life })}</span>
                  <div className="filter-bar">
                    <div
                      className={`filter-fill ${vmc.filter_life < 20 ? 'warning' : ''}`}
                      style={{ width: `${Math.min(100, (vmc.filter_life / FILTRO_BARRA_MAX) * 100)}%` }}
                    />
                  </div>
                  {vmc.filter_life < 20 && (
                    <span className="filter-warning">{t('vmc.substituirFiltro')}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="vmc-climate-comparison">
              <h3>{t('vmc.comparacao')}</h3>
              <div className="climate-bars">
                <div className="climate-item">
                  <span className="climate-label">{t('ac.temperatura')}</span>
                  <div className="climate-compare">
                    <div className="compare-bar">
                      <span>{t('vmc.interior')}</span>
                      <div className="bar indoor">
                        <div style={{ width: `${(vmc.temp_indoor / 30) * 100}%` }} />
                      </div>
                      <span>{vmc.temp_indoor}°C</span>
                    </div>
                    <div className="compare-bar">
                      <span>{t('vmc.exterior')}</span>
                      <div className="bar outdoor">
                        <div style={{ width: `${(vmc.temp_outdoor / 30) * 100}%` }} />
                      </div>
                      <span>{vmc.temp_outdoor}°C</span>
                    </div>
                  </div>
                </div>

                <div className="climate-item">
                  <span className="climate-label">{t('vmc.humidade')}</span>
                  <div className="climate-compare">
                    <div className="compare-bar">
                      <span>{t('vmc.interior')}</span>
                      <div className="bar indoor">
                        <div style={{ width: `${vmc.humidity_indoor}%` }} />
                      </div>
                      <span>{vmc.humidity_indoor}%</span>
                    </div>
                    <div className="compare-bar">
                      <span>{t('vmc.exterior')}</span>
                      <div className="bar outdoor">
                        <div style={{ width: `${vmc.humidity_outdoor}%` }} />
                      </div>
                      <span>{vmc.humidity_outdoor}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Vmc;
